import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../common/columnFilterSchema";

const pricingPlanStatsSchema = z.object({
  total_sales: z.number(),
  total_units: z.number(),
  page_views: z.number(),
  target_units: z.number(),
  target_sales: z.number(),
  inc_contribution: z.number(),
  inc_units: z.number(),
  inc_cashflow: z.number(),
  name: z.string().nullable(),
  tags: z
    .string()
    .transform((tags) => {
      try {
        return JSON.parse(tags) as string[];
      } catch {
        return [];
      }
    })
    .nullable(),
  min_price: z.number().nullable(),
  max_price: z.number().nullable(),
  best_price: z.number().nullable(),
  configured_slider_value: z.number().nullable(),
  plan_type: z.string().nullable(),
  status: z.string().nullable(),
  enroll_date: z.string().datetime({ local: true }).nullable(),
  is_auto: z.boolean().nullable(),
  override_supply: z.boolean().nullable(),
  status_reason: z.string().nullable(),
  category_id: z.number().nullable(),
});

export const pricingPlanReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4PricingPlanReportByPlan",
    method: "get",
    path: "/v4/query/?table=pricing_dashboard_v4&grouping=pricing_plan_id",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: z
          .array(
            z.object({
              id: z.string(),
              value: z.object({
                op: z.string().transform((op) => {
                  return op;
                }),
                value: z.unknown(),
              }),
            }),
          )
          .optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            pricing_plan_id: z.number(),
            name: z.string(),
            status: z.string(),
          })
          .merge(pricingPlanStatsSchema),
      ),
      cursor: z
        .object({
          hasPreviousPage: z.boolean(),
          hasNextPage: z.boolean(),
          startCursor: z.string().nullable(),
          endCursor: z.string().nullable(),
        })
        .nullable(),
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4PricingPlanReportByDate",
      method: "get",
      path: "/v4/query/?table=pricing_dashboard_v4&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(pricingPlanStatsSchema),
          // .merge(merchStatsSchema),
        ),
        cursor: z
          .object({
            hasPreviousPage: z.boolean(),
            hasNextPage: z.boolean(),
            startCursor: z.string().nullable(),
            endCursor: z.string().nullable(),
          })
          .nullable(),
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4PricingPlanReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=pricing_dashboard_v4",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: pricingPlanStatsSchema,
      }),
    })
    .addEndpoint({
      alias: "getV4PricingPlanReportByOptions",
      method: "get",
      path: "/v4/query/values/?table=pricing_dashboard_v4",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
        }),
      }),
    })
    .addEndpoint({
      alias: "getV4PricingPlanReportByExport",
      method: "get",
      path: "/v4/query/?table=product&xls=true&report_type=pricing_dashboard_v4",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .build(),
);
